import type { TransactionSubmissionFailure } from "./submission-error";

export const ORDER_EVENT_TYPES = {
  order: "market_types::OrderEvent",
  twap: "async_matching_engine::TwapEvent",
} as const;

export interface OrderEventClientOrderId {
  vec: unknown[];
}

export interface OrderEventStatus {
  __variant__: string;
}

export interface OrderEventTimeInForce {
  __variant__: string;
}

export interface OrderEventTriggerCondition {
  vec: unknown[];
}

export interface OrderEventOrderId {
  order_id: string;
}

export interface OrderEvent {
  client_order_id: OrderEventClientOrderId;
  details: string;
  is_bid: boolean;
  is_taker: boolean;
  market: string;
  metadata_bytes: string;
  order_id: string;
  orig_size: string;
  parent: string;
  price: string;
  remaining_size: string;
  size_delta: string;
  status: OrderEventStatus;
  time_in_force: OrderEventTimeInForce;
  trigger_condition: OrderEventTriggerCondition;
  user: string;
}

export interface TwapEvent {
  account: string;
  duration_s: string;
  frequency_s: string;
  is_buy: boolean;
  is_reduce_only: boolean;
  market: string;
  order_id: OrderEventOrderId;
  orig_size: string;
  remain_size: string;
  start_time_s: string;
  status: OrderEventStatus;
  client_order_id: OrderEventClientOrderId;
}

/**
 * Emitted by `spot_pending_cbs_queue` when a spot order's funding requires a
 * rate-limited CBS withdrawal: the transaction succeeds but the order is
 * queued, not resting. It is placed once `process_pending_withdrawals`
 * drains the request — poll `/orders?asset_type=spot` for the real
 * acknowledgment.
 */
export interface SpotOrderPendingCbsEvent {
  order_id: string;
  withdraw_request_id: string;
  subaccount_addr: string;
  market: string | { inner: string };
  price: string;
  orig_size: string;
  is_bid: boolean;
  metadata: string | { inner: string };
  pfs_balance: string;
  created_at: string;
}

/** `failedDecryption`: see `isFailedDecryptionError`; nothing executed, so a fresh resubmit is safe. */
interface FailedDecryptionFlags {
  failedDecryption?: true;
  encryptionEpoch?: string;
}

export type PlaceOrderResult =
  | {
      success: true;
      orderId: string | undefined;
      transactionHash: string;
    }
  | ({
      success: false;
      error: string;
      submission?: TransactionSubmissionFailure;
    } & FailedDecryptionFlags);

export type PlaceSpotOrderResult =
  | {
      success: true;
      orderId: string | undefined;
      /**
       * True when the order was queued behind a rate-limited CBS withdrawal
       * ({@link SpotOrderPendingCbsEvent}) instead of reaching the book in
       * this transaction. Poll the order endpoints for the real acknowledgment.
       */
      pendingCbs: boolean;
      transactionHash: string;
    }
  | ({
      success: false;
      error: string;
    } & FailedDecryptionFlags);
